import uuid from 'react-native-uuid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AllSettled, ApiLogger, IStatus } from '../../utils';
import { Platform } from 'react-native';
import get from 'lodash.get';
import { NewLoanInProgressConstants } from '../../constants';
const useTrackingItemsQuery = _ref => {
  let {
    url,
    accessToken,
    loanNumber,
    logApiEvent,
    select,
    enabled
  } = _ref;
  const correlationId = uuid.v4().toString();
  const trackingConfig = {
    method: 'get',
    url: `${url}/documents?index=${loanNumber}`,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'x-correlation-id': correlationId
    }
  };
  const historyConfig = {
    method: 'get',
    url: `${url}/documents/history?index=${loanNumber}`,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'channel': 'servicing',
      'division': 'digital_originations',
      'application': 'docupload',
      'x-correlation-id': correlationId
    }
  };
  return useQuery({
    queryKey: ['tracking-items', loanNumber],
    queryFn: () => AllSettled([ApiLogger(logApiEvent, trackingConfig), ApiLogger(logApiEvent, historyConfig)]),
    enabled: enabled,
    select
  });
};
const useUploadFileMutation = _ref2 => {
  let {
    url,
    accessToken,
    loanNumber,
    logApiEvent,
    setAlertDetails,
    setSelectedFile,
    logUserEvent,
    setInvalidateCache
  } = _ref2;
  const correlationId = uuid.v4().toString();
  return useMutation({
    mutationFn: _ref3 => {
      let {
        file,
        documentName
      } = _ref3;
      const webRequestBody = new FormData();
      webRequestBody.append('documentName', documentName);
      //@ts-ignore
      webRequestBody.append('file', new Blob([file]), file.name);
      const config = {
        method: 'post',
        url: `${url}/documents?index=${loanNumber}`,
        headers: {
          ...(Platform.OS !== 'web' ? {} : {
            'Content-Type': 'multipart/form-data'
          }),
          'Authorization': `Bearer ${accessToken}`,
          'x-correlation-id': correlationId
        },
        data: Platform.OS !== 'web' ? {
          content: documentName,
          attachments: [{
            title: file.name,
            content: file.content
          }]
        } : webRequestBody
      };
      return ApiLogger(logApiEvent, config);
    },
    onSuccess: async data => {
      if (data.responseDetails.uploadStatus === 'Failure') {
        setAlertDetails({
          toastType: 'ERROR',
          message: NewLoanInProgressConstants.UPLOAD_DOCUMENTS_ERROR_MESSAGE
        });
        logUserEvent({
          action: 'FileUploadResponseFailed',
          type: 'NewLoanInProgress',
          description: `UploadStatus: ${data.responseDetails.uploadStatus}`
        });
        await Promise.reject();
      } else {
        setAlertDetails({
          toastType: 'SUCCESS',
          message: NewLoanInProgressConstants.UPLOAD_DOCUMENTS_SUCCESS_MESSAGE
        });
        logUserEvent({
          action: 'DocumentFileUploadSuccess',
          type: 'NewLoanInProgress',
          description: `UploadStatus: ${data.responseDetails.uploadStatus}`
        });
        setInvalidateCache(true);
      }
      setSelectedFile(null);
    },
    onError: error => {
      setSelectedFile(null);
      setAlertDetails({
        toastType: 'ERROR',
        message: NewLoanInProgressConstants.UPLOAD_DOCUMENTS_ERROR_MESSAGE
      });
      logUserEvent({
        action: 'DocumentFileUploadFailed',
        type: 'NewLoanInProgress',
        description: `UploadStatus: ${error}`.substring(0, 198)
      });
    }
  });
};
const status = {
  'Pre gate Accepted': 'Pending Review',
  'Borrower Submitted': 'Pending Review',
  'Pre UW Accepted': 'Approved',
  'Completed': 'Approved',
  'Approved': 'Approved'
};
const transformToSections = data => {
  let sections = [];
  const [tracking, history] = data;
  let tasks = [];
  let taskStatus = [{
    title: 'Pending Review',
    data: [],
    itemIndex: 0,
    sectionIndex: 1
  }, {
    title: 'Approved',
    data: [],
    itemIndex: 1,
    sectionIndex: 1
  }, {
    title: 'Upload History',
    data: [],
    itemIndex: 2,
    sectionIndex: 1
  }];
  if (tracking && tracking.status === IStatus.fulfilled && tracking.value) {
    const responseDetails = get(tracking, ['value', 'responseDetails'], []);
    if (Array.isArray(responseDetails) && responseDetails.length) {
      tasks = tasks.concat(responseDetails.map(item => ({
        ...item,
        name: item.name ? item.name : item.trackingItemName
      })).filter(item => ['Outstanding', 'Pre UW Incomplete', 'Pre gate Incomplete'].includes(item.status)).map((trackingItem, itemIndex) => ({
        ...trackingItem,
        sectionIndex: 0,
        itemIndex,
        infoText: trackingItem.status === 'Pre UW Incomplete' || trackingItem.status === 'Pre gate Incomplete' ? 'There was an error with your uploaded document' : undefined,
        color: trackingItem.status === 'Pre UW Incomplete' || trackingItem.status === 'Pre gate Incomplete' ? '#D63D34' : '#D1D3D4'
      })));
      taskStatus = responseDetails.reduce((result, current) => {
        if (current) {
          const title = status[current.status];
          const idx = result.findIndex(each => each.title === title);
          if (title && idx >= 0) {
            const {
              length
            } = result[idx].data;
            const withIndex = {
              ...current,
              itemIndex: length,
              sectionIndex: 2
            };
            result[idx] = {
              ...result[idx],
              data: result[idx].data.concat(withIndex)
            };
            return result;
          }
        }
        return result;
      }, taskStatus);
    }
    tasks.push({
      trackingDate: '',
      trackingitemClass: '',
      name: 'Other Documents',
      status: 'Outstanding',
      sectionIndex: 0,
      itemIndex: tasks.length,
      details: 'Provide other documents requested by your Loan Advisor or Customer Advocate (if applicable).',
      trackingItemName: 'Additional Documents',
      infoText: undefined,
      color: undefined
    });
    sections.push({
      title: 'Your Tasks',
      description: 'In order to keep your loan on track for closing, you must complete the steps below.',
      data: tasks,
      sectionIndex: 0
    });
    sections.push({
      title: 'Document Status',
      sectionIndex: 1,
      description: 'Once all your tasks are complete, Freedom Mortgage will submit your documents to underwriting for review. The approved status means that your documents have satisfied our requirements.',
      data: taskStatus.map(each => ({
        ...each,
        title: `${each.title}`
      }))
    });
  }
  if (history && history.status === IStatus.fulfilled && history.value) {
    const responseDetails = get(history, ['value', 'responseDetails'], []);
    if (responseDetails) {
      sections[1].data[2] = {
        title: 'Upload History',
        data: Object.values(responseDetails).reduce((result, current) => {
          if (Array.isArray(current) && current.length) {
            const withIndexAndSection = current.map((each, index) => ({
              ...each,
              itemIndex: index,
              sectionIndex: 2,
              name: each.documentType,
              trackingDate: each.createdDate
            }));
            return [...result, ...withIndexAndSection];
          }
          return result;
        }, []),
        sectionIndex: 1,
        itemIndex: 2
      };
    }
  }
  return sections;
};
const useLoanOverviewQuery = _ref4 => {
  let {
    url,
    accessToken,
    logApiEvent,
    loanNumber
  } = _ref4;
  const correlationId = uuid.v4().toString();
  const config = {
    method: 'get',
    url: `${url}/customer/loan-applications/${loanNumber}`,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'channel': 'Correspondent',
      'division': 'Correspondent',
      'application': 'new-loan-in-progress',
      'x-correlation-id': correlationId
    }
  };
  return useQuery({
    queryKey: ['loan-overview', loanNumber],
    queryFn: () => ApiLogger(logApiEvent, config)
  });
};
const useLoanStatusQuery = _ref5 => {
  let {
    url,
    accessToken,
    logApiEvent,
    select,
    loanNumber,
    enabled
  } = _ref5;
  const correlationId = uuid.v4().toString();
  const config = {
    method: 'get',
    url: `${url}/statustracker/fm.com/track?index=${loanNumber}`,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'x-correlation-id': correlationId
    }
  };
  return useQuery({
    queryKey: ['loan-status-tracker', loanNumber],
    queryFn: () => ApiLogger(logApiEvent, config),
    select,
    enabled: enabled
  });
};
const DoneAndCompletePredicate = states => !states ? undefined : states.some(state => state.stepName.toLowerCase() === 'done' && state.status.toLowerCase() === 'complete');
const selectStatusTrackerResponse = data => data.responseDetails;
const selectClosingInformation = data => {
  const response = selectStatusTrackerResponse(data);
  if (!response) return undefined;
  return response.closingInformation;
};
const selectLoanScheduledClosingDate = data => {
  const response = selectClosingInformation(data);
  if (!response) return undefined;
  return response.loanScheduledClosingDate;
};
const selectTrackerDetails = data => {
  const response = selectStatusTrackerResponse(data);
  if (!response) return undefined;
  return response.tracker;
};
const selectTrackerStates = data => {
  const tracker = selectTrackerDetails(data);
  if (!tracker || !(Array.isArray(tracker.states) && tracker.states.length)) return undefined;
  return tracker.states;
};
const selectDoneOrCompleteStatus = data => {
  const states = selectTrackerStates(data);
  return DoneAndCompletePredicate(states);
};
const Helpers = {
  transformToSections
};
export { useTrackingItemsQuery, useUploadFileMutation, Helpers, useLoanOverviewQuery, selectDoneOrCompleteStatus, selectTrackerStates, selectTrackerDetails, selectLoanScheduledClosingDate, selectClosingInformation, selectStatusTrackerResponse, DoneAndCompletePredicate, useLoanStatusQuery };
//# sourceMappingURL=index.js.map